import { useState } from 'react'
import { api } from '../api/client'
import type { LivenessReport } from '../api/types'
import { useAsync } from '../api/useReport'
import { Banner } from '../components/Banner'
import { PulseStrip } from '../components/PulseStrip'

interface Props {
  propertyId?: string
}

/**
 * The tracking pulse, on its own.
 *
 * It used to sit above the daybook stream, where it competed with the day's numbers for
 * the top of the page and made the home screen answer two different questions at once —
 * "is collection working" and "what happened". Here it answers only the first, and the
 * daybook carries a one-line recap that links through.
 *
 * The page owns the fetch rather than the strip, so a pulse failure reports itself as a
 * failure of this page and nothing else. `pulseKey` forces a bypass of the server's
 * five-minute memo when the reader asks explicitly.
 */
export function Pulse({ propertyId }: Props) {
  const [pulseKey, setPulseKey] = useState(0)

  const { data, error, loading, validating } = useAsync<LivenessReport>(
    (signal) => api.pulse({ propertyId, refresh: pulseKey > 0 }, signal),
    [propertyId, pulseKey],
    true,
    `pulse:${propertyId ?? ''}:${pulseKey}`,
  )

  if (loading) {
    return (
      <div className="card">
        <p className="placeholder">Reading the last hours GA4 has processed…</p>
      </div>
    )
  }
  if (error) return <Banner tone="critical">{error}</Banner>
  if (!data) return null

  // The strip renders nothing without events, which is fine above a daybook full of
  // days and a blank page here. Say what is missing and where it is configured.
  if (data.events.length === 0) {
    return (
      <div className="card">
        <p className="placeholder">
          No events are being watched for this property yet. Add them under Settings →
          Tracked events, and each one is checked here against the hours GA4 has
          processed.
        </p>
      </div>
    )
  }

  return (
    <PulseStrip
      report={data}
      onRefresh={() => setPulseKey((key) => key + 1)}
      refreshing={validating}
    />
  )
}
